import GoogleMapReact from 'google-map-react'
import { useEffect, useMemo, useRef } from 'react'
import { renderToString } from 'react-dom/server'
import StoreCardItem from '../components/StoreCardItem'
import useStore from '../hooks/useStore'
import { LatLng, StoreItem } from '../types'

const CardWrapper = (props: any) => <div className="p-2" {...props} />

let selectedStoreId: any = null

const DEFAULT_CENTER = {
  lat: -37.840935,
  lng: 144.946457,
}

const NEXT_PUBLIC_GOOGLE_MAP_API =
  process.env.NEXT_PUBLIC_GOOGLE_MAP_API ?? null

interface Props {
  onReady?: () => void
}

export const MapContainer = ({ onReady }: Props) => {
  const [{ stores, currentPosition, yourStore }, { setAsStore }] = useStore()
  const googleMapRef = useRef({ map: null, maps: null })

  const defaultCenter = useMemo(() => {
    if (yourStore) {
      return yourStore?.location
    }
    return stores[0]?.location ?? DEFAULT_CENTER
  }, [stores, yourStore])

  useEffect(() => {
    selectedStoreId = yourStore?.id
  }, [yourStore])

  useEffect(() => {
    const googleMap = googleMapRef.current

    if (!googleMap?.map) return
    if (yourStore) return
    if (!currentPosition) return

    const { lat, lng } = currentPosition

    // @ts-ignore
    googleMap.map.setCenter(new googleMap.maps.LatLng(lat, lng))
    // @ts-ignore
    googleMap.map.setZoom(10)
  }, [currentPosition])

  useEffect(() => {
    const googleMap = googleMapRef.current

    if (!googleMap?.map) return
    if (currentPosition) return
    if (!yourStore) return

    const lat = yourStore?.location?.lat
    const lng = yourStore?.location?.lng

    // @ts-ignore
    googleMap.map.setCenter(new googleMap.maps.LatLng(lat, lng))
    // @ts-ignore
    googleMap.map.setZoom(10)
  }, [currentPosition, yourStore])

  const initGoogleApiLoaded = (
    map: { fitBounds: (arg0: any) => void },
    maps: {
      Size: new (arg0: number, arg1: number) => any
      Point: new (arg0: number, arg1: number) => any
      Marker: new (arg0: {
        position: LatLng | undefined
        icon: {
          url: string
          scaledSize: any // scaled size
          origin: any
        }
        map: any
      }) => any
      InfoWindow: new (arg0: { content: string }) => any
      event: {
        addListener: (arg0: any, arg1: string, arg2: () => void) => void
      }
      LatLngBounds: new () => any
    }
  ) => {
    const icon = {
      url: `/assets/icons/map-pin.svg`,
      scaledSize: new maps.Size(31.5, 40), // scaled size
      origin: new maps.Point(0, 0), // origin
    }

    var activeInfoWindow: { close: () => void }
    var activeSetStoreButton: HTMLElement | null

    const buildMarker = (store: StoreItem) => {
      const marker = new maps.Marker({
        position: store.location,
        icon,
        map,
      })

      const infoWindow = new maps.InfoWindow({
        content: renderToString(
          <CardWrapper>
            <StoreCardItem enableSetStore={true} {...store} idPrefix="map" />
          </CardWrapper>
        ),
      })

      const setButtonSelected = (
        button: HTMLElement | null,
        selected: boolean
      ) => {
        if (!button) return
        if (!selected) {
          button.classList.remove('bg-grey-5', 'under-button-black')
          button.classList.add(
            'bg-black',
            'text-white',
            'under-button-gradient'
          )
          button.innerText = 'Set as Store'
        } else {
          button.classList.remove(
            'bg-black',
            'text-white',
            'under-button-gradient'
          )
          button.classList.add('bg-grey-5', 'under-button-black')
          button.innerText = 'Your Store'
        }
      }

      const setStoreHandler = () => {
        setAsStore(store)
        const button = document.getElementById(`map-${store.id}`)
        setButtonSelected(button, true)
      }

      marker.addListener('click', () => {
        if (activeInfoWindow) {
          activeSetStoreButton?.removeEventListener('click', setStoreHandler)
          activeInfoWindow.close()
        }
        infoWindow.open(map, marker)
        infoWindow.setOptions({
          maxWidth: 300,
        })
        activeInfoWindow = infoWindow

        maps.event.addListener(infoWindow, 'domready', () => {
          activeSetStoreButton = document.getElementById(`map-${store.id}`)
          if (activeSetStoreButton) {
            setButtonSelected(
              activeSetStoreButton,
              store.id === selectedStoreId
            )
            activeSetStoreButton.addEventListener('click', setStoreHandler)
          }
        })
      })

      return marker
    }

    const markers = stores.map((store: StoreItem) => buildMarker(store))

    const bounds = new maps.LatLngBounds()

    for (let i = 0; i < markers.length; i++) {
      bounds.extend(markers[i].getPosition())
    }

    map.fitBounds(bounds)

    // @ts-ignore
    googleMapRef.current = { map, maps }

    onReady && onReady()
  }

  if (!NEXT_PUBLIC_GOOGLE_MAP_API) return <div />

  return (
    <div
      style={{
        width: '100%',
        height: 'calc(100vh - 80px)',
      }}
    >
      {defaultCenter && (
        <GoogleMapReact
          bootstrapURLKeys={{
            key: NEXT_PUBLIC_GOOGLE_MAP_API,
            libraries: ['places'],
            version: 'weekly',
            id: '__googleMapsScriptId',
          }}
          center={defaultCenter}
          defaultZoom={12}
          yesIWantToUseGoogleMapApiInternals
          onGoogleApiLoaded={({ map, maps }) => initGoogleApiLoaded(map, maps)}
        />
      )}
    </div>
  )
}
